import "./site/error-beacon";
import { Spire, DropEvent, BlockKind } from "./games/spire/spire";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.spire_pb";
const HANDLE_KEY = "aether.handle";

// Retry any submits that failed on a prior page load (transient 5xx / network).
flushFailedSubmits("spire");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startSpireRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/spire", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishSpireRun(runId: string, handle: string, drops: DropEvent[]) {
  const url = `/api/runs/spire/${runId}`;
  const body = { handle, drops };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("spire", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("spire", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudHeight = document.getElementById("hud-height")!;
const hudPerfects = document.getElementById("hud-perfects")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const kindBadge = document.getElementById("kind-badge") as HTMLElement | null;
const rescueOverlay = document.getElementById("rescue-overlay") as HTMLElement | null;
const btnRescueUse = document.getElementById("btn-rescue-use") as HTMLButtonElement | null;
const btnRescueSkip = document.getElementById("btn-rescue-skip") as HTMLButtonElement | null;
const rescueChip = document.getElementById("rescue-chip") as HTMLElement | null;
const milestoneBanner = document.getElementById("milestone-banner") as HTMLElement | null;
const recapHistogram = document.getElementById("recap-histogram") as HTMLElement | null;

const KIND_LABEL: Record<BlockKind, string> = {
  normal: "",
  magnetic: "MAGNETIC · wider perfect",
  gust: "GUST · fast sweep · ×2 bonus",
  perfect_only: "LOCKED · perfect or fail",
};
const KIND_CLASS: Record<BlockKind, string> = {
  normal: "",
  magnetic: "kind-magnetic",
  gust: "kind-gust",
  perfect_only: "kind-locked",
};

let game: Spire | null = null;
let activeRunId: string | null = null;
let pendingDrops: DropEvent[] | null = null;
let rescueResolve: ((use: boolean) => void) | null = null;

let milestoneTimer = 0;
function showMilestoneBanner(name: string, blocks: number, bonus: number) {
  if (!milestoneBanner) return;
  milestoneBanner.innerHTML = `<div class="ms-title">REACHED THE ${name.toUpperCase()}</div><div class="ms-sub">Block ${blocks} · +${bonus.toLocaleString()} pts</div>`;
  milestoneBanner.classList.add("visible");
  window.clearTimeout(milestoneTimer);
  milestoneTimer = window.setTimeout(() => milestoneBanner.classList.remove("visible"), 2600);
}

function renderRecapHistogram(faced: Record<BlockKind, number>, perfect: Record<BlockKind, number>) {
  if (!recapHistogram) return;
  const rows: { kind: BlockKind; label: string; cls: string }[] = [
    { kind: "normal",       label: "Normal",   cls: "normal" },
    { kind: "magnetic",     label: "Magnetic", cls: "magnetic" },
    { kind: "gust",         label: "Gust",     cls: "gust" },
    { kind: "perfect_only", label: "Locked",   cls: "locked" },
  ];
  const maxFaced = Math.max(1, ...rows.map(r => faced[r.kind]));
  recapHistogram.innerHTML = rows.map(r => {
    const f = faced[r.kind];
    const p = perfect[r.kind];
    const facedPct = (f / maxFaced) * 100;
    const perfPct = f > 0 ? (p / maxFaced) * 100 : 0;
    return `<div class="recap-row ${r.cls}">
      <div class="recap-label">${r.label}</div>
      <div class="recap-bar">
        <div class="recap-bar-fill" style="width:${facedPct.toFixed(1)}%"></div>
        <div class="recap-bar-perfect" style="width:${perfPct.toFixed(1)}%"></div>
      </div>
      <div class="recap-count">${f}<span class="perf"> · ${p}♦</span></div>
    </div>`;
  }).join("");
  recapHistogram.hidden = false;
}

function updateKindBadge(kind: BlockKind) {
  if (!kindBadge) return;
  if (KIND_LABEL[kind]) {
    kindBadge.textContent = KIND_LABEL[kind];
    kindBadge.className = `kind-badge ${KIND_CLASS[kind]}`;
    kindBadge.classList.add("visible");
  } else {
    kindBadge.classList.remove("visible");
  }
}

function showRescuePrompt() {
  if (!rescueOverlay) return;
  rescueOverlay.classList.add("visible");
  // Auto-dismiss after 2.8s — counts as "skip".
  const timeout = window.setTimeout(() => {
    if (rescueResolve) {
      rescueOverlay.classList.remove("visible");
      const r = rescueResolve;
      rescueResolve = null;
      r(false);
    }
  }, 2800);
  const finish = (use: boolean) => {
    window.clearTimeout(timeout);
    rescueOverlay.classList.remove("visible");
    const r = rescueResolve;
    rescueResolve = null;
    if (r) r(use);
  };
  btnRescueUse?.addEventListener("click", () => finish(true), { once: true });
  btnRescueSkip?.addEventListener("click", () => finish(false), { once: true });
}

import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";

async function startRun() {
  if (recapHistogram) {
    recapHistogram.hidden = true;
    recapHistogram.innerHTML = "";
  }
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startSpireRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Spire(canvas, {
    onHud: (state) => {
      hudHeight.textContent = String(state.height);
      hudPerfects.textContent = String(state.perfects);
      hudScore.textContent = state.scoreEstimate.toLocaleString();
      updateKindBadge(state.currentKind);
      if (rescueChip) rescueChip.classList.toggle("available", state.rescueAvailable);
    },
    onRescuePrompt: (resolve) => {
      rescueResolve = resolve;
      showRescuePrompt();
    },
    onMilestone: ({ blocks, name, bonus }) => {
      showMilestoneBanner(name, blocks, bonus);
    },
    onGameOver: async ({ drops, scoreEstimate, perfects, height, rescued, milestonesReached, kindFaced, kindPerfect }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = milestonesReached >= 4 ? "Reached the moon" : rescued ? "Tower toppled" : "Tower fell";
      const rescuedTag = rescued ? " · rescue used" : "";
      const milestonesTag = milestonesReached > 0 ? ` · ${milestonesReached}🌙 milestones` : "";
      msgBody.textContent = `Height ${height} · ${perfects} perfects · ${scoreEstimate.toLocaleString()} pts${rescuedTag}${milestonesTag}`;
      renderRecapHistogram(kindFaced, kindPerfect);
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "spire", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("spire", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishSpireRun(activeRunId, handle, drops);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingDrops = drops;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = dseed !== null ? `Spire · daily ${new URL(window.location.href).searchParams.get("daily")}` : "Spire";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingDrops) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishSpireRun(activeRunId, value, pendingDrops);
  pendingDrops = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
